import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { PerioMeasurement, PerioMeasurementsBySideDraft, ToothSide } from '@libs/ui-lib';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AbstractControlValue, subtractFallbackToZero } from '@libs/utils';
import { TranslateModule } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';
import { filter, skip, switchMap } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { PerioNumberConfigMeasurementComponent } from '../perio-number-config-measurement/perio-number-config-measurement.component';
import { PerioBooleanConfigMeasurementComponent } from '../perio-boolean-config-measurement/perio-boolean-config-measurement.component';
import { PerioFormControlService, PerioInputMode } from '../../services/perio-form-control/perio-form-control.service';
import { isReversedMeasurementOrder } from '../../../perio-exam-screen/services/perio-exam/utils';
import { HasFgPipe } from '../../pipes';
import {
  LOWER_RIGHT_START,
  UPPER_RIGHT_END,
} from '../../../perio-add-treatment-screen/perio-position-indicator/perio-position-indicator-constants';
import {
  PerioConfigFormGroup,
  PerioConfigFormGroupValueType,
  PerioMeasurementFormGroup,
  PerioMeasurementSurface,
  TargetIndexMovement,
} from './perio-config.vm';

@Component({
  selector: 'lib-perio-config',
  imports: [
    ReactiveFormsModule,
    PerioNumberConfigMeasurementComponent,
    PerioBooleanConfigMeasurementComponent,
    TranslateModule,
    NgTemplateOutlet,
    HasFgPipe,
  ],
  templateUrl: './perio-config.component.html',
  styleUrl: './perio-config.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerioConfigComponent implements OnInit {
  public disabled = input<boolean>(false);
  public showTitle = input<boolean>(false);
  public isCentralRecession = input<boolean>(false);
  public side = input.required<ToothSide>();
  public voiceModeActive = input.required<boolean>();
  public measurements = input.required<PerioMeasurementsBySideDraft>();
  public toothPosition = input.required<number>();
  public perioChanges = output<PerioMeasurementsBySideDraft>();
  public toothSidePerioDone = output<TargetIndexMovement>();

  protected reversedMeasurementOrder = computed(() => {
    return isReversedMeasurementOrder(this.toothPosition(), this.side());
  });

  protected isRightSide = computed(() => {
    return this.toothPosition() <= UPPER_RIGHT_END || this.toothPosition() >= LOWER_RIGHT_START;
  });

  protected isLeftSide = computed(() => !this.isRightSide());

  protected firstMeasurementSurfaceToFocus = computed(() => {
    return this.reversedMeasurementOrder() ? 'mesial' : 'distal';
  });
  protected lastMeasurementSurfaceToFocus = computed(() => {
    return this.reversedMeasurementOrder() ? 'distal' : 'mesial';
  });

  protected form: FormGroup<PerioConfigFormGroup> = new FormGroup({
    bleedingOnProbing: new FormGroup<PerioMeasurementFormGroup<boolean>>({
      distal: new FormControl<boolean>(false, { nonNullable: true }),
      central: new FormControl<boolean>(false, { nonNullable: true }),
      mesial: new FormControl<boolean>(false, { nonNullable: true }),
    }),
    suppuration: new FormGroup<PerioMeasurementFormGroup<boolean>>({
      distal: new FormControl<boolean>(false, { nonNullable: true }),
      central: new FormControl<boolean>(false, { nonNullable: true }),
      mesial: new FormControl<boolean>(false, { nonNullable: true }),
    }),
    pocketDepth: new FormGroup<PerioMeasurementFormGroup<number | undefined>>({
      distal: new FormControl<number | undefined>(undefined, { nonNullable: true }),
      central: new FormControl<number | undefined>(undefined, { nonNullable: true }),
      mesial: new FormControl<number | undefined>(undefined, { nonNullable: true }),
    }),
    rec: new FormGroup<PerioMeasurementFormGroup<number | undefined>>({
      distal: new FormControl<number | undefined>(undefined, { nonNullable: true }),
      central: new FormControl<number | undefined>(undefined, { nonNullable: true }),
      mesial: new FormControl<number | undefined>(undefined, { nonNullable: true }),
    }),
  });

  protected activePocketDepthCursor = signal<PerioMeasurementSurface | null>(null);
  protected activeSurface = signal<PerioMeasurementSurface | null>(null);
  protected activeRecessionCursor = signal<PerioMeasurementSurface | null>(null);
  protected inputMode = toSignal(inject(PerioFormControlService).getInputMode$(), { initialValue: 'pocketDepth' as PerioInputMode });
  private readonly perioFormControlService = inject(PerioFormControlService);
  private readonly destroyRef = inject(DestroyRef);
  private movedToPreviousToothByArrowKey = false;
  private movedToToothByArrowKey = false;

  constructor() {
    this.perioFormControlService.reset$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.form.reset(undefined, { emitEvent: false });
    });

    effect(() => {
      if (this.disabled()) {
        this.form.disable();
      } else {
        const measurements = untracked(this.measurements)!;
        const toReset = this.converMeasurementsToFormValue(measurements);
        this.form.reset(toReset, { emitEvent: false });
        this.form.enable();
      }
    });

    /**
     * This effect is responsible for resetting the form using the values we received through the measurements signal input
     * when the user has navigated to the next tooth (which is being told by the toothPosition signal input).
     */
    effect(() => {
      if (this.toothPosition()) {
        const measurements = untracked(() => this.measurements());
        if (measurements) {
          const toReset = this.converMeasurementsToFormValue(measurements);
          this.form.reset(toReset, { emitEvent: false });
        }
      }
    });

    /**
     * This effect is responsible for resetting the form when the measurements change
     * without the tooth position changing (e.g., when clearing measurements via voice commands).
     */
    effect(() => {
      const measurements = this.measurements();
      if (measurements && !this.disabled()) {
        const currentToothPosition = untracked(() => this.toothPosition());
        if (currentToothPosition) {
          const toReset = this.converMeasurementsToFormValue(measurements);
          this.form.reset(toReset, { emitEvent: false });
        }
      }
    });

    /**
     * This effect is responsible for focusing the first input field once the user moved towards the next tooth or switched sides.
     * It respects the current input mode (pocket depth or recession) to focus the appropriate field.
     */
    effect(() => {
      const currentPosition = this.toothPosition();
      const currentSide = this.side();

      if (currentPosition && currentSide && !this.disabled() && !untracked(this.voiceModeActive)) {
        // Read the computed signal that depends on both position and side
        const reversed = this.reversedMeasurementOrder();

        // We're setting the focus to the first input field and setting the last pressed digit to be the control value
        const preservedDigitFromPreviousTooth = this.perioFormControlService.getLastDigit();
        const surfaceToFocus: 'distal' | 'mesial' = this.movedToPreviousToothByArrowKey
          ? this.lastMeasurementSurfaceToFocus()
          : this.firstMeasurementSurfaceToFocus();

        // Check input mode and focus appropriate field
        const currentMode = this.perioFormControlService.getInputMode();
        if (currentMode === 'recession') {
          // In central-only recession mode, always focus on central field
          const recessionSurfaceToFocus = this.isCentralRecession() ? 'central' : surfaceToFocus;
          this.perioFormControlService.focusRecession(
            recessionSurfaceToFocus,
            reversed,
            TargetIndexMovement.None,
            this.isCentralRecession(),
          );
        } else {
          this.perioFormControlService.focusPocketDepth(
            surfaceToFocus,
            reversed,
            TargetIndexMovement.None,
            preservedDigitFromPreviousTooth,
            true,
          );
        }

        this.movedToPreviousToothByArrowKey = false;
        this.movedToToothByArrowKey = false;
      }
    });
  }

  public ngOnInit(): void {
    this.form.valueChanges
      .pipe(
        skip(1),
        filter(() => !this.disabled()),
      )
      .subscribe((value) => this.emitPerioChange(value as PerioConfigFormGroupValueType));

    this.perioFormControlService
      .getToothDoneTrigger$(this.destroyRef, this.disabled)
      .pipe(switchMap(() => this.perioFormControlService.getLastCursorMovementDirection$()))
      .subscribe((direction) => {
        console.warn('tooth done', direction);
        this.movedToPreviousToothByArrowKey = direction === TargetIndexMovement.Prev;
        this.toothSidePerioDone.emit(direction);
      });

    this.perioFormControlService.getMovedWithoutValue$(this.destroyRef).subscribe(() => {
      this.movedToToothByArrowKey = true;
    });

    this.perioFormControlService
      .getActivePocketDepthCursor$()
      .pipe(filter(() => !this.disabled()))
      .subscribe((activeCursor) => {
        this.activePocketDepthCursor.set(activeCursor);
      });

    this.perioFormControlService
      .getActiveCursor$()
      .pipe(filter(() => !this.disabled()))
      .subscribe((activeSurface) => {
        this.activeSurface.set(activeSurface);
      });

    this.perioFormControlService
      .getActiveRecessionCursor$()
      .pipe(filter(() => !this.disabled()))
      .subscribe((activeRecessionCursor) => {
        this.activeRecessionCursor.set(activeRecessionCursor);
      });

    // Listen for input mode changes and refocus appropriately
    this.perioFormControlService
      .getInputMode$()
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((mode) => {
        if (!this.disabled()) {
          // Use queueMicrotask to ensure DOM is ready and change detection has completed
          queueMicrotask(() => {
            const reversed = this.reversedMeasurementOrder();
            if (mode === 'recession') {
              const isCentral = this.isCentralRecession();
              const recessionSurfaceToFocus = isCentral ? 'central' : this.firstMeasurementSurfaceToFocus();
              this.perioFormControlService.focusRecession(recessionSurfaceToFocus, reversed, TargetIndexMovement.None, isCentral);

              setTimeout(() => {
                this.perioFormControlService.setActiveRecessionCursor(recessionSurfaceToFocus);
                this.perioFormControlService.setActiveCursor(recessionSurfaceToFocus);
              }, 0);
            } else {
              const surfaceToFocus = this.firstMeasurementSurfaceToFocus();
              this.perioFormControlService.focusPocketDepth(surfaceToFocus, reversed, TargetIndexMovement.None, undefined, true);

              setTimeout(() => {
                this.perioFormControlService.setActivePocketDepthCursor(surfaceToFocus, false, reversed);
                this.perioFormControlService.setActiveCursor(surfaceToFocus);
              }, 0);
            }
          });
        }
      });
  }

  private emitPerioChange(value: PerioConfigFormGroupValueType) {
    const measurements = this.measurements();
    this.perioChanges.emit({
      bleedingOnProbing: {
        distal: value.bleedingOnProbing!.distal,
        central: value.bleedingOnProbing!.central,
        mesial: value.bleedingOnProbing!.mesial,
      },
      suppuration: {
        distal: value.suppuration!.distal,
        central: value.suppuration!.central,
        mesial: value.suppuration!.mesial,
      },
      pocketDepth: {
        distal: value.pocketDepth!.distal ?? undefined,
        central: value.pocketDepth!.central ?? undefined,
        mesial: value.pocketDepth!.mesial ?? undefined,
      },
      clinicalAttachmentLoss: {
        distal: subtractFallbackToZero(+value.pocketDepth!.distal!, -+value.rec!.distal!),
        central: subtractFallbackToZero(+value.pocketDepth!.central!, -+value.rec!.central!),
        mesial: subtractFallbackToZero(+value.pocketDepth!.mesial!, -+value.rec!.mesial!),
      },
      gingivalMargin: {
        distal: value.rec!.distal ?? undefined,
        central: value.rec!.central ?? undefined,
        mesial: value.rec!.mesial ?? undefined,
      },
      furcation: measurements!.furcation,
    });
  }

  private converMeasurementsToFormValue(measurements: PerioMeasurementsBySideDraft): AbstractControlValue<FormGroup<PerioConfigFormGroup>> {
    return {
      bleedingOnProbing: measurements.bleedingOnProbing as PerioMeasurement<boolean>,
      suppuration: measurements.suppuration as PerioMeasurement<boolean>,
      pocketDepth: {
        distal: measurements.pocketDepth!.distal ?? undefined,
        central: measurements.pocketDepth!.central ?? undefined,
        mesial: measurements.pocketDepth!.mesial ?? undefined,
      },
      rec: {
        distal: measurements.gingivalMargin!.distal ?? undefined,
        central: measurements.gingivalMargin!.central ?? undefined,
        mesial: measurements.gingivalMargin!.mesial ?? undefined,
      },
    };
  }
}
